import Phaser from 'phaser';
import type { ContractFixtureEnvelope } from '../../contract/types';
import { computeSplitBarLayout } from '../world/splitBar';
import type { SessionConfig } from '../world/types';
import { actionTypeForSide } from '../world/types';
import { GAME_PALETTE, GAME_TIMING } from '../theme';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import { HUD_SCENE_KEY, WORLD_SCENE_KEY } from '../sceneKeys';
import { a11yBus, A11Y_EVENTS } from '../../accessibility/a11yBus';

export interface HudSceneData {
  contractVendored: boolean;
  fixture?: ContractFixtureEnvelope;
  sessionConfig: SessionConfig;
}

/**
 * Fixed-overlay HUD (CP21 "What to build" #5): the progress-split bar
 * rendered from the loaded CP14-contract-fixture side totals (a visual
 * consistency check against the core app's own CP11 hero bar rendering of
 * the same §9 data - acceptance criterion 6). Also shows quota remaining
 * and the current side/action, updated live via events from WorldScene.
 */
export class HudScene extends Phaser.Scene {
  private quotaText!: Phaser.GameObjects.Text;
  private toastText!: Phaser.GameObjects.Text;

  constructor() {
    super(HUD_SCENE_KEY);
  }

  create(data: HudSceneData): void {
    const fixture = data.fixture;
    const beorningsPct = fixture?.sides.beornings.sharePct ?? 50;
    const lothwenPct = fixture?.sides.lothwen.sharePct ?? 50;

    this.drawSplitBar(beorningsPct, lothwenPct);
    this.drawSessionInfo(data);
    this.drawContractStatus(data.contractVendored, fixture);

    // CP22 fix: this is the one place that knows side + quota + split-bar %
    // together at session boot - broadcast it once so the accessible DOM
    // layer's aria-live region can announce it to assistive tech (Vera's
    // CRITICAL finding: none of this HUD state had any DOM/ARIA equivalent).
    a11yBus.emit(A11Y_EVENTS.SESSION_START, {
      side: data.sessionConfig.side,
      quota: data.sessionConfig.quota,
      beorningsPct,
      lothwenPct,
    });

    this.quotaText = this.add
      .text(GAME_WIDTH - 20, 60, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#eaf5ec',
      })
      .setOrigin(1, 0);

    this.toastText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 90, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#ffd27f',
        backgroundColor: '#08210f',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(0.5)
      .setAlpha(0);

    const world = this.scene.get(WORLD_SCENE_KEY);
    world.events.on('quotaChanged', (quota: number) => {
      this.quotaText.setText(`Quota: ${quota}`);
    });
    world.events.on('toast', (message: string) => {
      this.showToast(message);
    });
  }

  private drawSplitBar(beorningsPct: number, lothwenPct: number): void {
    const barWidth = GAME_WIDTH - 40;
    const barX = 20;
    const barY = 16;
    const barHeight = 18;
    const layout = computeSplitBarLayout(beorningsPct, lothwenPct, barWidth);

    this.add.rectangle(barX, barY, barWidth, barHeight, GAME_PALETTE.hudBarTrack).setOrigin(0, 0);
    // Lothwen grows from the left edge toward center; Beornings grows from the right edge toward center -
    // matching the world's own "men walk right, women walk left from the city" framing.
    this.add
      .rectangle(barX, barY, layout.lothwenWidthPx, barHeight, GAME_PALETTE.hudBarLothwen)
      .setOrigin(0, 0);
    this.add
      .rectangle(barX + barWidth, barY, layout.beorningsWidthPx, barHeight, GAME_PALETTE.hudBarBeornings)
      .setOrigin(1, 0);

    this.add
      .text(barX, barY + barHeight + 4, `Lothwen ${lothwenPct.toFixed(1)}%`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#c9f0d3',
      })
      .setOrigin(0, 0);
    this.add
      .text(barX + barWidth, barY + barHeight + 4, `Beornings ${beorningsPct.toFixed(1)}%`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#f0d3c9',
      })
      .setOrigin(1, 0);
  }

  private drawSessionInfo(data: HudSceneData): void {
    const action = actionTypeForSide(data.sessionConfig.side);
    const label = data.sessionConfig.side === 'beornings' ? 'Beornings (chop)' : 'Lothwen (plant)';
    this.add
      .text(20, 60, `${label} - ${action} to advance the frontier`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#eaf5ec',
      })
      .setOrigin(0, 0);

    if (data.sessionConfig.hrOverCeiling) {
      this.add
        .text(20, 80, 'HR over MAF ceiling - the trees sense it (harder hits this session)', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#e08a7b',
        })
        .setOrigin(0, 0);
    }

    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 8, 'Arrows/A-D or on-screen buttons to move - SPACE/ACT to chop or plant', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#9fd3ac',
      })
      .setOrigin(0.5, 1);
  }

  private drawContractStatus(contractVendored: boolean, fixture?: ContractFixtureEnvelope): void {
    const text = contractVendored
      ? `Contract fixture: ${fixture?.season?.name ?? 'unknown (no active season)'}${fixture?.draft ? ' (DRAFT - pre-CP14)' : ''}`
      : 'No contract fixture vendored yet (CP14 pending)';
    this.add
      .text(20, GAME_HEIGHT - 8, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#7b9fe0',
      })
      .setOrigin(0, 1);
  }

  private showToast(message: string): void {
    this.toastText.setText(message);
    this.tweens.killTweensOf(this.toastText);
    this.toastText.setAlpha(1);
    this.tweens.add({
      targets: this.toastText,
      alpha: 0,
      delay: GAME_TIMING.toastDurationMs,
      duration: 300,
    });
  }
}
